import clsx from "clsx";
import millify from "millify";
import { MapCard } from "../Card/mapCard";
import { debounce, groupBy } from "lodash";
import useSupercluster from "use-supercluster";
import { PiBuildingBold } from "react-icons/pi";
import { IoCloseOutline } from "react-icons/io5";
import type { PointFeature } from "supercluster";
import { IoIosCloseCircleOutline } from "react-icons/io";
import HouseIcon from "../../../assets/icons/houseIcon.svg";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMapFiltersStore } from "../../../stores/filterMapStore";
import { usePaginationStore } from "../../../stores/paginationStore";
import { selectedCitiesCoordinates } from "../../../citiesWithLatLong";
import { useFilteredPropertiesStore } from "../../../stores/exploreListings";
import { mapPropertyTypeForPost } from "../../common/FilterPanel/filterPanel";
import { type Coordinates, useMapStore } from "../../../stores/mapCordinations";
import { PropertyTypeEnum } from "../../../interfaces/buyerMatchingPreferences";
import { useSearchStore } from "../../../stores/newStores/exploreListingSearchStore";
import { getDistanceFromLatLonInMeters } from "../../../utils/getDistanceFromLatLonInMeters";
import {
  convertToFormattedAmount,
  convertToNumberAndMultiplyBy100,
} from "../../../utils/convertAmount";
import ReactMapGL, {
  GeolocateControl,
  Marker,
  NavigationControl,
  type MapRef,
} from "react-map-gl";
import {
  LastStatusEnum,
  type ListingCard,
  type ListingFiltersInterface,
  fetchExploreListings,
  normalizeToUndefined,
  parseActiveListing,
  parseBasement,
  parseDeListedListing,
  parseSoldListing,
  transformFilterValues,
} from "../../../services/exploreListings";

import "swiper/css";
import "mapbox-gl/dist/mapbox-gl.css";

interface PositionParams {
  position: { x: number; y: number };
  viewportWidth: number;
  viewportHeight: number;
  size: number;
}

interface PositionResult {
  dynamicTop: number;
  dynamicLeft: number;
}

interface PointProperties extends ListingCard {
  cluster: false;
  propertyId: string | number | null;
  unit: ListingCard[];
  countUnit: number | null;
}

const MAPBOX_TOKEN =
  "pk.eyJ1IjoiYmVhcmNvZGVzODcwIiwiYSI6ImNtMHBmMjVqZzAwZWkycXEwYmcxcHJjMGwifQ.UeHMFKKaRuaTR-NqOPPsnw";

interface SearchMapViewProps {
  setLoading: (loading: boolean) => void;
  loading: boolean;
}

const SearchMapView = ({ loading, setLoading }: SearchMapViewProps) => {
  const [properties, setProperties] = useState<ListingCard[]>();
  const {
    currentPage,
    setCurrentPage,
    resetPage,
    setPageStep,
    setPageStepList,
  } = usePaginationStore();
  const [clickedProperties, setClickedProperties] = useState<ListingCard[]>([]);
  const [isNavigatingToView, setIsNavigatingToView] = useState(false);

  const { setFilteredProperties } = useFilteredPropertiesStore();

  interface MapViewState {
    latitude: number;
    longitude: number;
    zoom: number;
    bearing: number;
    pitch: number;
  }

  const { setMapState, lastKnownLocation, setLastKnownLocation } =
    useMapStore();
  const initialViewState: MapViewState = {
    longitude: lastKnownLocation[0]?.lng || 0,
    latitude: lastKnownLocation[0]?.lat || 0,
    zoom: 10,
    bearing: 0,
    pitch: 0,
  };

  const [viewState, setViewState] = useState<MapViewState>(initialViewState);
  const [markerClicked, setMarkerClicked] = useState(false);

  // const fetchProperties = useCallback(() => {
  // 	setProperties(mockListings);
  // 	setFilteredProperties(mockListings);
  // }, [setFilteredProperties]);

  const mapRef = useRef<MapRef | null>(null);
  const {
    minPrice,
    maxPrice,
    beds,
    baths,
    dens,
    parking,
    propertyType,
    numberOfKitchens,
    garageType,
    basement,
    // openHouseType,
    style,
    daysOnMarket,
    mapSelectedCities,
    reloadMapCities,
    setSortFilter,
    sortFilter,
    setReloadMapCities,
    keywordSearch,
    propertyPrice,
    propertySquareFootage,
    propertyMaintenanceFee,
    selectedOptions,
    togglesPropertyType,
    sortValue,
    switchMode,
    isDropdownOpen,
  } = useMapFiltersStore();

  const { coordinatesLatLng, limitMap } = useMapStore();

  const { setSearchValue } = useSearchStore();

  const onMapLoad = () => {
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [lastKnownLocation[0].lng, lastKnownLocation[0].lat],
        zoom: 10,
        essential: true,
        duration: 2000,
      });
    }
  };

  useEffect(() => {
    onMapLoad();
  }, [coordinatesLatLng]);

  useEffect(() => {
    if (coordinatesLatLng && mapRef.current) {
      const [longitude, latitude] = coordinatesLatLng.map(Number);
      if (!isNaN(longitude) && !isNaN(latitude)) {
        if (mapRef.current) {
          mapRef.current.flyTo({
            center: [longitude, latitude],
            zoom: 12,
            essential: true,
          });
          setLastKnownLocation(coordinatesLatLng);
        }
      }
    }
  }, [coordinatesLatLng]);

  const fetchPropertiesBasedOnView = useCallback(async () => {
    if (mapRef.current && !isNavigatingToView) {
      const map = mapRef.current.getMap();
      const { lng, lat } = map.getCenter();
      const bounds = map.getBounds();
      const northEast = bounds?.getNorthEast();

      if (!northEast) {
        return;
      }

      const radius = getDistanceFromLatLonInMeters(
        lng,
        lat,
        northEast.lng,
        northEast.lat
      );

      const filters: ListingFiltersInterface = {
        active: parseActiveListing(selectedOptions.active.value, {
          active: togglesPropertyType.active,
        }),
        sold: parseSoldListing(selectedOptions.sold.value, {
          sold: togglesPropertyType.sold,
        }),
        delisted: parseDeListedListing(selectedOptions.de_listed.value, {
          de_listed: togglesPropertyType.de_listed,
        }),
        numberOfBedrooms: normalizeToUndefined(
          beds.length > 0
            ? beds.every((value) => value === "All")
              ? null
              : transformFilterValues(beds)
            : null
        ),
        numberOfBathrooms: normalizeToUndefined(
          baths.length > 0
            ? baths.every((value) => value === "All")
              ? null
              : transformFilterValues(baths)
            : null
        ),
        numberOfParkingSpaces: normalizeToUndefined(
          parking.length > 0
            ? parking.every((value) => value === "All")
              ? null
              : transformFilterValues(parking)
            : null
        ),
        propertyType: normalizeToUndefined(
          propertyType.length > 0
            ? propertyType.every((value) => value === "All types")
              ? null
              : propertyType
                  .map(mapPropertyTypeForPost)
                  .filter((pt): pt is PropertyTypeEnum => pt !== null)
            : null
        ),
        dens: normalizeToUndefined(dens),
        keyword: normalizeToUndefined(keywordSearch),
        basement: parseBasement(basement),
        // openHouse: parseOpenHouse(openHouseType),
        priceRange: [
          convertToNumberAndMultiplyBy100(propertyPrice[0].toString()),
          convertToNumberAndMultiplyBy100(propertyPrice[1].toString()),
        ],
        sqftRange: [
          Number(propertySquareFootage[0].toString()),
          Number(propertySquareFootage[1].toString()),
        ],
        maintenanceFeeRange: [
          convertToNumberAndMultiplyBy100(propertyMaintenanceFee[0].toString()),
          convertToNumberAndMultiplyBy100(propertyMaintenanceFee[1].toString()),
        ],
      };
      setLoading(true);
      const fetchedProperties = await fetchExploreListings(
        currentPage.toString(),
        limitMap.toString(),
        sortValue,
        sortFilter.sortOrder,
        lng,
        lat,
        radius,
        undefined,
        filters
      );

      setPageStep(1);
      setPageStepList(1);

      setLoading(false);
      if (fetchedProperties) {
        setProperties(fetchedProperties);
        setMapState(lng, lat, radius);

        setFilteredProperties(fetchedProperties);
        setSearchValue("");
      }
    }
  }, [
    currentPage,
    parking,
    setFilteredProperties,
    minPrice,
    maxPrice,
    beds,
    baths,
    dens,
    parking,
    propertyType,
    daysOnMarket,
    basement,
    setMapState,
    sortFilter,
    propertyPrice,
    selectedOptions.sold,
    selectedOptions.active,
    togglesPropertyType.sold,
    togglesPropertyType.active,
  ]);

  const debouncedFetchProperties = useCallback(
    debounce(async () => {
      try {
        const filters: ListingFiltersInterface = {
          active: parseActiveListing(selectedOptions.active.value, {
            active: togglesPropertyType.active,
          }),
          sold: parseSoldListing(selectedOptions.sold.value, {
            sold: togglesPropertyType.sold,
          }),
          delisted: parseDeListedListing(selectedOptions.de_listed.value, {
            de_listed: togglesPropertyType.de_listed,
          }),
          numberOfBedrooms: normalizeToUndefined(
            beds.length > 0
              ? beds.every((value) => value === "All")
                ? null
                : transformFilterValues(beds)
              : null
          ),
          numberOfBathrooms: normalizeToUndefined(
            baths.length > 0
              ? baths.every((value) => value === "All")
                ? null
                : transformFilterValues(baths)
              : null
          ),
          numberOfParkingSpaces: normalizeToUndefined(
            parking.length > 0
              ? parking.every((value) => value === "All")
                ? null
                : transformFilterValues(parking)
              : null
          ),
          propertyType: normalizeToUndefined(
            propertyType.length > 0
              ? propertyType.every((value) => value === "All types")
                ? null
                : propertyType
                    .map(mapPropertyTypeForPost)
                    .filter((pt): pt is PropertyTypeEnum => pt !== null)
              : null
          ),
          dens: normalizeToUndefined(dens),
          keyword: normalizeToUndefined(keywordSearch),
          basement: parseBasement(basement),
          priceRange: [
            convertToNumberAndMultiplyBy100(propertyPrice[0].toString()),
            convertToNumberAndMultiplyBy100(propertyPrice[1].toString()),
          ],
          sqftRange: [
            Number(propertySquareFootage[0].toString()),
            Number(propertySquareFootage[1].toString()),
          ],
          maintenanceFeeRange: [
            convertToNumberAndMultiplyBy100(
              propertyMaintenanceFee[0].toString()
            ),
            convertToNumberAndMultiplyBy100(
              propertyMaintenanceFee[1].toString()
            ),
          ],
        };

        if (mapRef.current) {
          const map = mapRef.current.getMap();
          const { lng, lat } = map.getCenter();
          const bounds = map.getBounds();
          const northEast = bounds?.getNorthEast();

          if (!northEast) {
            return;
          }

          const radius = getDistanceFromLatLonInMeters(
            lng,
            lat,
            northEast.lng,
            northEast.lat
          );
          setLoading(true);
          const fetchedProperties = await fetchExploreListings(
            currentPage.toString(),
            limitMap.toString(),
            sortValue,
            sortFilter.sortOrder,
            lng,
            lat,
            radius,
            undefined,
            filters
          );

          setLoading(false);
          if (fetchedProperties) {
            setProperties(fetchedProperties);
            setFilteredProperties(fetchedProperties);
            moveToSelectedCity();
          }
        }
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    }, 1000),
    [
      currentPage,
      switchMode,
      selectedOptions.sold,
      selectedOptions.active,
      togglesPropertyType.sold,
      togglesPropertyType.active,
      propertyPrice,
      setFilteredProperties,
      minPrice,
      maxPrice,
      beds,
      baths,
      propertyType,
      daysOnMarket,
      style,
      sortFilter,
    ]
  );

  useEffect(() => {
    setCurrentPage(1);
    setPageStep(1);
    setPageStepList(1);
  }, [
    selectedOptions.sold,
    selectedOptions.active,
    togglesPropertyType.sold,
    togglesPropertyType.active,
    propertyPrice,
    setFilteredProperties,
    minPrice,
    maxPrice,
    beds,
    baths,
    dens,
    parking,
    propertyType,
    daysOnMarket,
    style,
    sortFilter,
    basement,
  ]);

  const moveToSelectedCity = useCallback(() => {
    if (mapSelectedCities.length > 0) {
      const selectedCity = mapSelectedCities[0];
      const cityCoordinates = selectedCitiesCoordinates[selectedCity];
      if (cityCoordinates && mapRef.current) {
        const [longitude, latitude] = cityCoordinates.map(Number);
        mapRef.current.flyTo({
          center: [longitude, latitude],
          zoom: 10,
          essential: true,
        });
      }
    }
  }, [mapSelectedCities]);

  useEffect(() => {
    const { lat, lng } = lastKnownLocation[0];

    if (lat === 43.70011 && lng === -79.4163) {
      checkAndNavigateToLocation();
    }
    setLoading(true);
    debouncedFetchProperties();

    return () => debouncedFetchProperties.cancel();
  }, [debouncedFetchProperties]);

  const points: PointFeature<PointProperties>[] = Object.entries(
    groupBy(properties, (property) => `${property?.long},${property?.lat}`)
  ).map(([coordinates, groupedProperties]) => {
    const [long, lat] = coordinates.split(",").map(Number);

    return {
      type: "Feature",
      properties: {
        cluster: false,
        propertyId:
          groupedProperties.length === 1
            ? groupedProperties[0].listingId
            : null,
        unit: groupedProperties,
        countUnit: groupedProperties.length,
        ...groupedProperties[0],
      },
      geometry: {
        type: "Point",
        coordinates: [long, lat],
      },
    };
  });

  const map = mapRef.current?.getMap();

  const checkAndNavigateToLocation = useCallback(() => {
    setIsNavigatingToView(true);
    const updatedLocation: Coordinates[] = [
      {
        lng: initialViewState.longitude,
        lat: initialViewState.latitude,
      },
    ];
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLastKnownLocation([
            {
              lng: position.coords.longitude,
              lat: position.coords.latitude,
            },
          ]);
          if (mapRef.current) {
            mapRef.current.flyTo({
              center: [position.coords.longitude, position.coords.latitude],
              zoom: 10,
              duration: 2000,
            });
          }
        },
        (error) => {
          console.warn("Location access denied or an error occurred:", error);
          setLastKnownLocation(updatedLocation);
        }
      );
    } else {
      console.warn("Geolocation is not supported by this browser.");
      setLastKnownLocation(updatedLocation);
    }
    setIsNavigatingToView(false);
  }, []);

  let bounds: [number, number, number, number] | undefined;

  if (map) {
    const mapBounds = map.getBounds();
    if (mapBounds) {
      bounds = mapBounds.toArray().flat() as [number, number, number, number];
    }
  }

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom: viewState.zoom,
    options: { radius: 75, maxZoom: 20 },
  });

  const handleOnMove = useCallback(
    debounce((event: { viewState: MapViewState }) => {
      const newViewState = event.viewState;

      setViewState(newViewState);
      const updatedLocation: Coordinates[] = [
        {
          lng: newViewState.longitude,
          lat: newViewState.latitude,
        },
      ];

      setLastKnownLocation(updatedLocation);
    }, 10),
    []
  );

  const handleOnMoveEnd = useCallback(() => {
    if (!markerClicked) {
      setClickedProperties([]);
    }
    setMarkerClicked(false);

    if (!isNavigatingToView) {
      fetchPropertiesBasedOnView();
    }
  }, [fetchPropertiesBasedOnView, markerClicked]);

  // useEffect(() => {
  //   const { lat, lng } = lastKnownLocation[0];

  //   if (lat === 43.70011 && lng === -79.4163) {
  //     checkAndNavigateToLocation();
  //   }
  // }, [checkAndNavigateToLocation]);

  const handleMarkerClick = useCallback(
    (clusterId: number | null, longitude: number, latitude: number) => {
      setMarkerClicked(true);
      if (clusterId) {
        const clusterExpansionZoom =
          supercluster?.getClusterExpansionZoom(clusterId);
        mapRef.current?.flyTo({
          center: [longitude, latitude],
          zoom: clusterExpansionZoom,
        });
        const clusteredPoints = supercluster?.getLeaves(
          clusterId,
          Number.POSITIVE_INFINITY
        );
      } else {
        const clickedProperty = properties.find(
          (property) =>
            property?.long === longitude && property?.lat === latitude
        );
        if (clickedProperty) {
          setClickedProperties([clickedProperty]);
        }
      }
    },
    [supercluster, properties]
  );

  const renderCustomMarker = useCallback((property: ListingCard) => {
    return (
      <div
        className={clsx(
          "relative font-medium text-[17px] border-white border-4 rounded-full px-2 py-1 shadow-lg text-white flex gap-2 items-center justify-center",
          property.lastStatus === LastStatusEnum.Sold ||
            property.lastStatus === LastStatusEnum.SoldConditionally ||
            property.lastStatus ===
              LastStatusEnum.SoldConditionallyWithEscapeClause
            ? "bg-[#C10230]"
            : "bg-z-dark-moss-green"
        )}
      >
        {property.propertyType === PropertyTypeEnum.Condo ||
        property.propertyType === PropertyTypeEnum.CondoTownhouse ? (
          <PiBuildingBold />
        ) : (
          <img src={HouseIcon} alt="House Icon" className="w-[16.49px]" />
        )}
        {property.listPrice && (
          <span>
            $
            {millify(convertToFormattedAmount(property.listPrice)) ||
              "Property"}
            <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[12px] border-transparent border-t-white absolute -bottom-[14px] left-1/2 -translate-x-1/2">
              <div
                className={clsx(
                  "w-0 h-0 border-l-[8px] border-r-[8px] border-t-[14px] border-transparent absolute -bottom-[4px] pt-2 left-1/2 -translate-x-1/2",
                  property.lastStatus === LastStatusEnum.Sold ||
                    property.lastStatus === LastStatusEnum.SoldConditionally ||
                    property.lastStatus ===
                      LastStatusEnum.SoldConditionallyWithEscapeClause
                    ? "border-t-[#C10230]"
                    : "border-t-z-dark-moss-green"
                )}
              />
            </div>
          </span>
        )}
      </div>
    );
  }, []);

  const renderCustomGroupedMarker = useCallback((properties: ListingCard[]) => {
    const anySold = properties.every(
      (sold) =>
        sold.lastStatus === LastStatusEnum.Sold ||
        sold.lastStatus === LastStatusEnum.SoldConditionally ||
        sold.lastStatus === LastStatusEnum.SoldConditionallyWithEscapeClause
    );

    return (
      <div
        className={clsx(
          "relative font-medium text-[17px] border-white border-4 rounded-full px-2 py-1 shadow-lg text-white flex gap-2 items-center justify-center",
          anySold ? "bg-[#C10230]" : "bg-z-dark-moss-green"
        )}
      >
        <PiBuildingBold />
        <span>{properties.length} units</span>
        <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[12px] border-transparent border-t-white absolute -bottom-[14px] left-1/2 -translate-x-1/2">
          <div
            className={clsx(
              "w-0 h-0 border-l-[8px] border-r-[8px] border-t-[14px] border-transparent absolute -bottom-[4px] pt-2 left-1/2 -translate-x-1/2",
              anySold ? "border-t-[#C10230]" : "border-t-z-dark-moss-green"
            )}
          />
        </div>
      </div>
    );
  }, []);

  const renderClusterMarker = useCallback(
    (pointCount: number, anySold: boolean | undefined) => {
      return (
        <div
          className={clsx(
            "cluster-marker border-white bg-z-dark-moss-green border-4 shadow-lg text-white rounded-full w-[50px] h-[50px] text-xl flex items-center justify-center",
            anySold ? "!bg-[#C10230]" : "bg-z-dark-moss-green"
          )}
        >
          {pointCount}
        </div>
      );
    },
    []
  );

  const getDynamicPosition = ({
    position,
    viewportWidth,
    viewportHeight,
    size,
  }: PositionParams): PositionResult => {
    let dynamicTop = position.y;
    let dynamicLeft = position.x;

    if (position.x + size > viewportWidth) {
      dynamicLeft = viewportWidth - size - 10;
    } else if (position.x < 0) {
      dynamicLeft = 10;
    }

    if (position.y + size > viewportHeight) {
      dynamicTop = viewportHeight - size - 10;
    } else if (position.y < 0) {
      dynamicTop = 10;
    }

    return { dynamicTop, dynamicLeft };
  };

  return (
    <div className="absolute bottom-0 top-[172px] inset-x-0">
      <ReactMapGL
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        onLoad={onMapLoad}
        ref={mapRef}
        initialViewState={viewState}
        minZoom={1}
        onMove={handleOnMove}
        onMoveEnd={handleOnMoveEnd}
        style={{ width: "100%", height: "100%" }}
      >
        <NavigationControl position="bottom-right" showZoom={true} />
        <GeolocateControl
          position="bottom-right"
          showUserLocation={true}
          trackUserLocation={true}
          style={{ marginTop: 50 }}
        />

        {clusters.map((cluster) => {
          const [longitude, latitude] = cluster.geometry.coordinates;

          const {
            point_count: pointCount,
            cluster_id: clusterId,
            cluster: isCluster,
            unit,
            countUnit,
          } = cluster.properties;

          if (isCluster) {
            const clusteredPoints = supercluster?.getLeaves(
              clusterId,
              Number.POSITIVE_INFINITY
            );
            const totalUnits = clusteredPoints?.reduce((sum, point) => {
              return sum + (point.properties.unit?.length || 0);
            }, 0);
            const anySold = clusteredPoints?.some((point) => {
              const lastStatus = point.properties.unit?.[0]?.lastStatus;
              return (
                lastStatus === LastStatusEnum.Sold ||
                lastStatus === LastStatusEnum.SoldConditionally ||
                lastStatus === LastStatusEnum.SoldConditionallyWithEscapeClause
              );
            });

            return (
              <Marker
                key={`cluster-${clusterId}`}
                longitude={longitude}
                latitude={latitude}
              >
                <button
                  type="button"
                  onClick={() =>
                    handleMarkerClick(clusterId, longitude, latitude)
                  }
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  {renderClusterMarker(totalUnits || pointCount, anySold)}
                </button>
              </Marker>
            );
          }
          if (unit.length > 1) {
            return (
              <Marker
                key={`${longitude}-${latitude}`}
                longitude={longitude}
                latitude={latitude}
              >
                <button
                  type="button"
                  onClick={() => {
                    setMarkerClicked(true);
                    setClickedProperties(unit);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  {renderCustomGroupedMarker(unit)}
                </button>
              </Marker>
            );
          }

          return (
            <Marker
              key={`${longitude}-${latitude}`}
              longitude={longitude}
              latitude={latitude}
              offset={[0, -25]}
            >
              <button
                type="button"
                onClick={() => {
                  setMarkerClicked(true);
                  setClickedProperties(unit);
                }}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                {renderCustomMarker(unit[0])}
              </button>
            </Marker>
          );
        })}
      </ReactMapGL>
      {clickedProperties.length > 0 &&
        clickedProperties.map((property) => {
          const { long, lat } = property;

          const position = mapRef.current.project([long, lat]);
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight - 172;
          const isSingleProperty = clickedProperties.length === 1;
          const size = isSingleProperty ? 360 : 500;
          const { dynamicTop, dynamicLeft } = getDynamicPosition({
            position,
            viewportWidth,
            viewportHeight,
            size,
          });

          if (clickedProperties.length === 1) {
            return (
              <div
                key={property.listingId}
                className="absolute"
                style={{
                  top: `${dynamicTop + 20}px`,
                  left: `${dynamicLeft}px`,
                }}
              >
                <div className="w-[320px]">
                  <div
                    className="absolute top-3 z-10 right-3 bg-white rounded-full w-6 h-6 flex items-center justify-center cursor-pointer"
                    onClick={() => setClickedProperties([])}
                    onKeyDown={() => setClickedProperties([])}
                  >
                    <IoCloseOutline className="text-xl" />
                  </div>
                  <div
                    className="flex justify-center w-[320px]"
                    key={property.listingId}
                  >
                    <MapCard property={property} />
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div
              key={property.listingId}
              className="max-w-[480px] rounded-2xl bg-white pl-5 pt-4 absolute"
              style={{ top: `${dynamicTop + 20}px`, left: `${dynamicLeft}px` }}
            >
              <div className="flex items-center justify-between pr-5">
                <span>{clickedProperties.length} Properties Selected</span>
                <IoIosCloseCircleOutline
                  className="text-2xl cursor-pointer"
                  onClick={() => setClickedProperties([])}
                />
              </div>
              <div className="overflow-y-auto max-h-[430px] pb-4 mt-4 space-y-4 pr-4 mr-1">
                {clickedProperties.map((property) => (
                  <div key={property.listingId}>
                    <div
                      className="flex justify-center shadow-none"
                      key={property.listingId}
                    >
                      <MapCard property={property} handleShadow={false} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export { SearchMapView };
